import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ToastProvider } from './context/ToastContext';
import { SettingsProvider } from './context/SettingsContext';

// Public pages
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import PaymentCallback from './pages/PaymentCallback';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import MyOrders from './pages/MyOrders';
import OrderDetail from './pages/OrderDetail';
import Contact from './pages/Contact';
import Services from './pages/Services';
import Consultancy from './pages/Consultancy';
import NotFound from './pages/NotFound';

// Admin pages
import AdminLayout from './components/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import AdminProducts from './pages/admin/Products';
import AdminProductForm from './pages/admin/ProductForm';
import AdminOrders from './pages/admin/Orders';
import AdminOrderDetail from './pages/admin/OrderDetail';
import AdminUsers from './pages/admin/Users';
import AdminCategories from './pages/admin/Categories';
import AdminMessages from './pages/admin/Messages';
import AdminSettings from './pages/admin/Settings';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import ScrollToTop from './components/ScrollToTop';

import './App.css';

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main className="main-content">{children}</main>
    <Footer />
  </>
);

function App() {
  return (
    <SettingsProvider>
      <ToastProvider>
        <AuthProvider>
          <CartProvider>
            <Router>
              <ScrollToTop />
              <div className="App">
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                  <Route path="/products" element={<PublicLayout><Products /></PublicLayout>} />
                  <Route path="/products/:id" element={<PublicLayout><ProductDetail /></PublicLayout>} />
                  <Route path="/cart" element={<PublicLayout><Cart /></PublicLayout>} />
                  <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
                  <Route path="/register" element={<PublicLayout><Register /></PublicLayout>} />
                  <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                  <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
                  <Route path="/consultancy" element={<PublicLayout><Consultancy /></PublicLayout>} />

                  {/* Customer-only routes */}
                  <Route element={<PrivateRoute />}>
                    <Route path="/checkout" element={<PublicLayout><Checkout /></PublicLayout>} />
                    <Route path="/payment-callback" element={<PublicLayout><PaymentCallback /></PublicLayout>} />
                    <Route path="/order-confirmation/:id" element={<PublicLayout><OrderConfirmation /></PublicLayout>} />
                    <Route path="/profile" element={<PublicLayout><Profile /></PublicLayout>} />
                    <Route path="/my-orders" element={<PublicLayout><MyOrders /></PublicLayout>} />
                    <Route path="/my-orders/:id" element={<PublicLayout><OrderDetail /></PublicLayout>} />
                  </Route>

                  {/* Admin routes */}
                  <Route path="/admin" element={<PrivateRoute roles={['admin', 'manager', 'editor']} />}>
                    <Route element={<AdminLayout />}>
                      <Route index element={<Dashboard />} />
                      <Route path="products" element={<AdminProducts />} />
                      <Route path="products/new" element={<AdminProductForm />} />
                      <Route path="products/edit/:id" element={<AdminProductForm />} />
                      <Route path="orders" element={<AdminOrders />} />
                      <Route path="orders/:id" element={<AdminOrderDetail />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="categories" element={<AdminCategories />} />
                      <Route path="messages" element={<AdminMessages />} />
                      <Route path="settings" element={<AdminSettings />} />
                    </Route>
                  </Route>

                  <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
                </Routes>
              </div>
            </Router>
          </CartProvider>
        </AuthProvider>
      </ToastProvider>
    </SettingsProvider>
  );
}

export default App;
